// React Query cache keys, in one place so a mutation's invalidation cannot drift from
// the query it refreshes. Hierarchical: invalidating `wellWorkspaceKeys.all` refreshes
// the listing, the summary and every open detail, which is what create and promote want.

export const wellWorkspaceKeys = {
  all: ['well-workspaces'] as const,
  // Without a query the key is the prefix every page shares, which is what invalidation
  // uses. With one it carries the search and sort too, not just the page — all three are
  // resolved server-side, so each combination is its own cached result.
  list: (query?: {
    page: number;
    pageSize: number;
    text?: string;
    sortAttribute?: string;
    sortDirection?: string;
    boundingBox?: unknown;
    polygonGeojson?: unknown;
  }) => {
    return query
      ? ([...wellWorkspaceKeys.all, 'list', query] as const)
      : ([...wellWorkspaceKeys.all, 'list'] as const);
  },
  summary: () => [...wellWorkspaceKeys.all, 'summary'] as const,
  detail: (id: string | number) =>
    [...wellWorkspaceKeys.all, 'detail', String(id)] as const,
  compare: (id: string | number) =>
    [...wellWorkspaceKeys.all, 'compare', String(id)] as const,
  // Every well's links, as one prefix. A promotion changes the status of a workspace on
  // whichever wells it holds, and the mutation should not have to enumerate them — so it
  // invalidates this and every open Well page's drawer, count and banner refresh.
  links: () => [...wellWorkspaceKeys.all, 'links'] as const,
  // The workspaces one well belongs to: the Well page's Workspaces drawer, the View
  // menu's counts and the promoted banner.
  linksFor: (uwi: string) => [...wellWorkspaceKeys.links(), uwi] as const,
};

export const wellsKeys = {
  all: ['wells'] as const,
  summary: () => [...wellsKeys.all, 'summary'] as const,
  // The payload rides in the key so changing a filter is a new query rather than a
  // refetch of the old one — React Query then serves the previous result while the
  // new one loads instead of flashing an empty table.
  query: (payload: Record<string, unknown>) =>
    [...wellsKeys.all, 'query', payload] as const,
  // One canonical well, as the workspace's scoped-well read fetches it.
  detail: (uwi: string) => [...wellsKeys.all, 'detail', uwi] as const,
};

// The unit candidates for one well authored in a workspace. Keyed by the staged-well id, the
// same address the endpoint takes — an authored well has no uwi worth keying a cache on, since
// the synthetic one carries no meaning to anything else.
export const wellUnitCandidateKeys = {
  all: ['well-unit-candidates'] as const,
  list: (workspaceId: number, stagedWellId: number) =>
    [...wellUnitCandidateKeys.all, workspaceId, stagedWellId] as const,
};

// The geometries a bore can be driven from, read under one Well Workspace. Marking one
// current changes which row the flag sits on, so the write invalidates this key.
export const wellboreGeometryKeys = {
  all: ['wellbore-geometries'] as const,
  list: (wellWorkspaceId: number, uwi14: string) =>
    [...wellboreGeometryKeys.all, 'list', wellWorkspaceId, uwi14] as const,
};

// The Add Wellbore tearsheet's LTD catalog. The whole query rides the key — facets, text,
// bbox and paging are all resolved server-side, so each combination is its own cached result
// and changing a filter serves the previous page while the next loads.
export const ltdCandidateKeys = {
  all: ['ltd-candidates'] as const,
  list: (query: unknown) => [...ltdCandidateKeys.all, query] as const,
};
